import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Reveal, Chapter } from "@/components/lunar/Reveal";

const API = process.env.REACT_APP_BACKEND_URL;

const COPY = {
    kit: {
        heading: "Request your Lunar kit.",
        sub: "No payment now — we’ll confirm availability, pricing and next steps.",
        button: "Request your Lunar kit",
        messageLabel: "Anything we should know?",
        messagePlaceholder: "Quantities, co-branding, delivery notes…",
    },
    bespoke: {
        heading: "Chat with us.",
        sub: "Tell us what your venue needs — we’ll come back with pricing and next steps.",
        button: "Start the conversation",
        messageLabel: "What does your venue need?",
        messagePlaceholder: "Venue type, materials, co-branding, delivery notes…",
    },
};

const inputCls =
    "w-full rounded-xl border border-[#C2C0B8] bg-transparent px-4 py-3 text-sm text-[#1D201A] placeholder:text-[#5C5F58] focus:border-[#4A5D23] focus:outline-none";
const labelCls = "mb-1.5 block text-xs font-medium uppercase tracking-[0.14em] text-[#5C5F58]";

export const RequestForm = ({ pkg, items, intent }) => {
    const copy = COPY[intent] || COPY.kit;
    const [form, setForm] = useState({
        name: "",
        email: "",
        venue_name: "",
        comments: "",
        phone: "",
        organisation: "",
        location: "",
        venue_type: "",
        capacity: "",
        required_by: "",
        cobranded: "",
    });
    const [showDetails, setShowDetails] = useState(false);
    const [status, setStatus] = useState("idle");
    const confirmationRef = useRef(null);

    useEffect(() => {
        if (status !== "sent") return;
        confirmationRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        confirmationRef.current?.focus({ preventScroll: true });
    }, [status]);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
    const products = Object.entries(items)
        .map(([name, value]) => `${name}: ${value}`)
        .join("; ");

    const submit = async (e) => {
        e.preventDefault();
        setStatus("sending");
        try {
            const res = await fetch(`${API}/api/venue-request`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...form, package: pkg, products }),
            });
            if (!res.ok) throw new Error("send failed");
            setStatus("sent");
        } catch {
            setStatus("idle");
            toast.error("Something went wrong sending your request — please try again.");
        }
    };

    if (status === "sent") {
        return (
            <section id="request-form" className="mx-auto max-w-3xl scroll-mt-8 px-6 py-24" data-testid="request-form-section">
                <Reveal>
                    <div
                        ref={confirmationRef}
                        tabIndex={-1}
                        role="status"
                        aria-live="polite"
                        data-testid="request-confirmation"
                        className="rounded-3xl border border-[#899B6A] bg-[#D5D8CB]/40 p-10 text-center focus:outline-none sm:p-14"
                    >
                        <h2 className="font-display text-3xl font-medium text-[#2C3618] sm:text-4xl">
                            Thanks — we’ve received your request.
                        </h2>
                        <p className="mt-4 text-base text-[#3E413B]">
                            We’ll review what you need and get back to you with pricing and next steps.
                        </p>
                    </div>
                </Reveal>
            </section>
        );
    }

    return (
        <section id="request-form" className="mx-auto max-w-3xl scroll-mt-8 px-6 py-24" data-testid="request-form-section">
            <Reveal>
                <Chapter index="03" label="Talk to us" />
                <h2
                    data-testid="request-form-heading"
                    className="font-display mt-5 text-4xl font-medium leading-[1.1] tracking-tight text-[#1D201A] sm:text-5xl"
                >
                    {copy.heading}
                </h2>
                <p className="mt-5 max-w-xl text-base leading-relaxed text-[#5C5F58]">{copy.sub}</p>
                {(pkg || products) && (
                    <div className="mt-6 flex flex-wrap items-center gap-2">
                        {pkg && (
                            <span data-testid="request-package-chip" className="rounded-full bg-[#2C3618] px-4 py-2 text-xs font-bold text-[#F9F8F4]">
                                {pkg}
                            </span>
                        )}
                        {products && (
                            <span data-testid="request-products-summary" className="rounded-full border border-[#C2C0B8] px-4 py-2 text-xs text-[#3E413B]">
                                {products}
                            </span>
                        )}
                    </div>
                )}
            </Reveal>
            <Reveal delay={0.1}>
                <form onSubmit={submit} className="mt-8 grid gap-5" data-testid="venue-request-form">
                    <div className="grid gap-5 sm:grid-cols-2">
                        <div>
                            <label className={labelCls}>
                                Name <span className="text-[#4A5D23]">*</span>
                            </label>
                            <input required data-testid="venue-form-name" className={inputCls} value={form.name} onChange={set("name")} />
                        </div>
                        <div>
                            <label className={labelCls}>
                                Email <span className="text-[#4A5D23]">*</span>
                            </label>
                            <input required type="email" data-testid="venue-form-email" className={inputCls} value={form.email} onChange={set("email")} />
                        </div>
                    </div>
                    <div>
                        <label className={labelCls}>
                            Venue / organisation name <span className="text-[#4A5D23]">*</span>
                        </label>
                        <input required data-testid="venue-form-venue-name" className={inputCls} value={form.venue_name} onChange={set("venue_name")} />
                    </div>
                    <div>
                        <label className={labelCls}>{copy.messageLabel}</label>
                        <textarea
                            rows={4}
                            data-testid="venue-form-message"
                            className={inputCls}
                            placeholder={copy.messagePlaceholder}
                            value={form.comments}
                            onChange={set("comments")}
                        />
                    </div>

                    <button
                        type="button"
                        data-testid="venue-form-details-toggle"
                        onClick={() => setShowDetails((v) => !v)}
                        aria-expanded={showDetails}
                        className="flex items-center gap-2 text-sm font-medium text-[#4A5D23] transition-colors duration-200 hover:text-[#2C3618]"
                    >
                        <span className={`inline-block transition-transform duration-200 ${showDetails ? "rotate-90" : ""}`}>›</span>
                        {showDetails ? "Fewer details" : "Add more details (optional)"}
                    </button>

                    {showDetails && (
                        <div className="grid gap-5 rounded-2xl border border-[#E5E3DC] p-5 sm:grid-cols-2" data-testid="venue-form-details">
                            <div>
                                <label className={labelCls}>Phone</label>
                                <input type="tel" data-testid="venue-form-phone" className={inputCls} value={form.phone} onChange={set("phone")} />
                            </div>
                            <div>
                                <label className={labelCls}>Organisation</label>
                                <input data-testid="venue-form-organisation" className={inputCls} value={form.organisation} onChange={set("organisation")} />
                            </div>
                            <div>
                                <label className={labelCls}>Location</label>
                                <input data-testid="venue-form-location" className={inputCls} value={form.location} onChange={set("location")} />
                            </div>
                            <div>
                                <label className={labelCls}>Capacity / expected attendance</label>
                                <input data-testid="venue-form-capacity" className={inputCls} value={form.capacity} onChange={set("capacity")} />
                            </div>
                            <div>
                                <label className={labelCls}>Materials needed by</label>
                                <input type="date" data-testid="venue-form-required-by" className={inputCls} value={form.required_by} onChange={set("required_by")} />
                            </div>
                            <div className="sm:col-span-2">
                                <label className={labelCls}>Co-branded materials?</label>
                                <div className="flex gap-2">
                                    {["Yes", "No"].map((opt) => (
                                        <button
                                            key={opt}
                                            type="button"
                                            data-testid={`venue-form-cobranding-${opt.toLowerCase()}`}
                                            onClick={() => setForm((f) => ({ ...f, cobranded: f.cobranded === opt ? "" : opt }))}
                                            aria-pressed={form.cobranded === opt}
                                            className={`rounded-full border px-5 py-2 text-sm font-medium transition-colors duration-200 ${
                                                form.cobranded === opt
                                                    ? "border-[#2C3618] bg-[#2C3618] text-[#F9F8F4]"
                                                    : "border-[#C2C0B8] text-[#3E413B] hover:border-[#899B6A]"
                                            }`}
                                        >
                                            {opt}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    <div>
                        <button
                            type="submit"
                            data-testid="venue-form-submit"
                            disabled={status === "sending"}
                            className="w-full rounded-full bg-[#2C3618] py-4 text-sm font-bold text-[#F9F8F4] transition-[background-color,transform] duration-300 hover:-translate-y-[2px] hover:bg-[#4A5D23] disabled:opacity-60 sm:w-auto sm:px-12"
                        >
                            {status === "sending" ? "Sending…" : copy.button}
                        </button>
                    </div>
                </form>
            </Reveal>
        </section>
    );
};
